/**
 * DecisionPlanOfRecordLink — the Plan of Record a decision belongs to.
 *
 * A Plan of Record is the highest commitment the organization makes (canon 27:
 * its force is directive — it binds the organization). A decision that stands
 * behind one says so at full size, by name, with the version in force and a
 * way to open it — never as one more small chip among the others.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getActivePlanOfRecord, getPlanCycleById } from '@/lib/planningService';
import { decisionRelatedObjectHref } from '@/types/prioritization/decisions';
import { AppCard, Eyebrow } from '@/apps/components/primitives';
import { formatDecisionDate } from './decisionRecordText';

interface PlanSummary {
  name: string | null;
  versionNumber: number | null;
  authorizedAt: string | null;
  /** False when the record of what is in force could not be read. */
  inForceRead: boolean;
}

export function DecisionPlanOfRecordLink({ planCycleId }: { planCycleId: string }) {
  const [plan, setPlan] = useState<PlanSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    void (async () => {
      const [cycle, active] = await Promise.all([
        getPlanCycleById(planCycleId),
        getActivePlanOfRecord(planCycleId),
      ]);
      if (cancelled) return;
      setPlan({
        name: cycle.data?.name?.trim() || null,
        versionNumber: active.data?.plan_version?.version_number ?? null,
        authorizedAt: active.data?.authorized_at ?? null,
        inForceRead: active.error === null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [planCycleId]);

  const href = decisionRelatedObjectHref({ kind: 'plan_cycle', id: planCycleId }) ?? '/planning';

  // What is in force, said only when it was read: a plan with no authorized
  // version is a plan, and this must not call it more than that.
  const inForce =
    plan?.versionNumber != null
      ? `Version ${plan.versionNumber} in force${
          plan.authorizedAt ? ` · authorized ${formatDecisionDate(plan.authorizedAt)}` : ''
        }`
      : plan?.inForceRead
        ? 'No version has been authorized yet'
        : null;

  return (
    <AppCard>
      <Eyebrow>Plan of Record</Eyebrow>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="font-heading text-xl font-semibold text-[var(--text-pri)]">
            {plan?.name ?? 'Plan of Record'}
          </p>
          <p className="mt-1 font-body text-sm text-[var(--text-sec)]">
            The highest commitment the organization makes. This decision stands behind it.
          </p>
          {inForce ? (
            <p className="mt-2 font-mono text-[11px] uppercase tracking-wider text-[var(--text-ter)]">
              {inForce}
            </p>
          ) : null}
        </div>
        <Link
          to={href}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[var(--text-pri)] px-4 py-2 font-body text-sm font-semibold text-[var(--text-pri)] hover:bg-[var(--hover-tint)]"
        >
          Open the Plan of Record
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </AppCard>
  );
}
